// §4.7 Capacity load (`/planning/capacity`, P3-I-2, D-401): per work centre
// and ISO week of the published schedule's horizon, or per day at the day
// grain, a bar of placed setup and run against the capacity mark from
// §2.19a's stored rows, the due-date load as a dot, the rough-cut proposed
// load hatched after the placed bar ("Proposed, not firmed"), and a period
// with an open overload in red with its hours and a link to the board on that
// lane and week. A toggle reads the waiting draft as differences from the
// published schedule. Read on `planning.schedule.read`. At 412 px each work
// centre is a card with its periods as rows, and nothing scrolls sideways.

import { useEffect, useMemo } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { CheckboxField } from "../../../../components/field";
import { SegmentedControl } from "../../../../components/select";
import { CardSkeleton, EmptyState, ErrorState } from "../../../../components/states";
import { addDaysIso } from "../../../../components/bucket-grid-paste";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { useAssignments, useCalendarWorkCenters } from "../hooks/calendar-queries";
import { usePlanningPermissions } from "../hooks/permissions";
import {
  useCapacityLoad,
  useScheduleHeads,
  type CapacityLoadRow,
  type ScheduleHeader,
} from "../hooks/schedule-queries";
import { formatHours, workCenterLabel } from "../lib/calendar-lib";
import { formatDate, weekLabel } from "../lib/planning-lib";
import {
  barOf,
  loadByWorkCenter,
  loadDelta,
  validateCapacitySearch,
  weekOf,
  type CapacityGrain,
  type CapacitySearch,
  type LoadRowLike,
} from "../lib/schedule-lib";
import { PlanningHeader } from "../components/planning-header";
import { usePlanningFacility } from "../planning-layout";
import { useSolveControl } from "../components/schedule-bits";
import { Hatch, ScheduleHatchDefs } from "../components/schedule-lanes";
import { shortId } from "../../../../lib/short-id";

/** The days one day-grain window shows. */
const DAY_WINDOW = 14;

/** The window of a schedule's horizon the grain reads, held inside the horizon. */
export function capacityWindow(
  header: Pick<ScheduleHeader, "horizon_start" | "horizon_days">,
  grain: CapacityGrain,
  wanted: string | undefined,
): { from: string; to: string } {
  const start = header.horizon_start;
  const last = addDaysIso(start, header.horizon_days - 1);
  if (grain === "week") return { from: start, to: last };
  let from = wanted ?? start;
  const latest = addDaysIso(last, -(DAY_WINDOW - 1));
  if (from > latest) from = latest;
  if (from < start) from = start;
  const to = addDaysIso(from, DAY_WINDOW - 1);
  return { from, to: to > last ? last : to };
}

export function PlanningCapacityScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { facility } = usePlanningFacility();
  const perms = usePlanningPermissions();
  const canPublish = perms.has("planning.schedule.publish");
  const canCalendars = perms.has("planning.calendar.read");
  const rawSearch: Record<string, unknown> = useSearch({ strict: false });
  const search = validateCapacitySearch(rawSearch);
  const grain: CapacityGrain = search.grain ?? "week";
  const heads = useScheduleHeads(facility.id);
  const published = heads.data?.published ?? null;
  const draft = heads.data?.draft ?? null;
  const showDraft = search.draft === true && draft !== null && published !== null;
  const window = published === null ? undefined : capacityWindow(published, grain, search.from);
  const load = useCapacityLoad(
    facility.id,
    published?.id,
    window ?? { from: "", to: "" },
    grain,
    window !== undefined,
  );
  const draftLoad = useCapacityLoad(
    facility.id,
    draft?.id,
    window ?? { from: "", to: "" },
    grain,
    showDraft && window !== undefined,
  );
  const workCenters = useCalendarWorkCenters(facility.id);
  const assignments = useAssignments(facility.id, undefined, undefined, canCalendars);
  const solve = useSolveControl(facility.id, { canSolve: canPublish });

  const authError = [heads.error, load.error, draftLoad.error].find(
    (e) => e instanceof UnauthorizedError,
  );
  useEffect(() => {
    if (authError !== undefined) void navigate({ to: "/login" });
  }, [authError, navigate]);

  const setSearch = (next: Partial<CapacitySearch>) => {
    const merged: CapacitySearch = { ...search, ...next };
    const compact = Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== undefined));
    void navigate({ to: "/planning/capacity", search: compact, replace: true });
  };

  const pageHead = <PlanningHeader title={t("planning.capacity.title")} actions={solve.button} />;

  const readError = heads.error ?? load.error;
  if (readError !== null && authError === undefined) {
    return (
      <section className="planning-screen planning-capacity" data-testid="planning-capacity">
        {pageHead}
        <ErrorState
          title={t("planning.capacity.loadError")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => {
            void heads.refetch();
            void load.refetch();
          }}
          requestId={requestIdOf(readError)}
          requestIdLabel={t("planning.common.requestId")}
        />
      </section>
    );
  }

  const settling =
    heads.isPending ||
    (published !== null && load.isPending) ||
    workCenters.isPending ||
    (canCalendars && published === null && assignments.isPending);
  if (settling) {
    return (
      <section className="planning-screen planning-capacity" data-testid="planning-capacity">
        {pageHead}
        <div data-testid="planning-capacity-loading">
          <CardSkeleton lines={6} label={t("planning.capacity.loading")} />
        </div>
      </section>
    );
  }

  const noCalendars = canCalendars && assignments.data?.items.length === 0;
  if (published === null || window === undefined) {
    return (
      <section className="planning-screen planning-capacity" data-testid="planning-capacity">
        {pageHead}
        {solve.banner}
        {noCalendars ? (
          <div data-testid="planning-capacity-no-calendars">
            <EmptyState
              title={t("planning.capacity.noCalendarsTitle")}
              body={t("planning.capacity.noCalendarsBody")}
              ctaLabel={t("planning.capacity.openCalendars")}
              onCta={() => {
                void navigate({ to: "/planning/settings/calendars" });
              }}
            />
          </div>
        ) : (
          <div data-testid="planning-capacity-empty">
            <EmptyState
              title={t("planning.capacity.emptyTitle")}
              body={t("planning.capacity.emptyBody")}
              {...(canPublish && !solve.running
                ? { ctaLabel: t("planning.schedule.solve.button"), onCta: solve.start }
                : {})}
            />
          </div>
        )}
      </section>
    );
  }

  const rows = load.data?.rows ?? [];
  const allZero = rows.length > 0 && rows.every((r) => r.capacity_minutes === 0);
  const deltas =
    showDraft && draftLoad.data !== undefined ? loadDelta(draftLoad.data.rows, rows) : undefined;
  const byWc = loadByWorkCenter(rows);
  const deltaByWc = deltas === undefined ? undefined : loadByWorkCenter(deltas);
  const wcName = (id: string) => {
    const wc = workCenters.data?.find((w) => w.id === id);
    return wc === undefined ? shortId(id) : workCenterLabel(wc, i18n.language);
  };
  const ordered = [...byWc.keys()].sort((a, b) => {
    const ca = workCenters.data?.find((w) => w.id === a)?.code ?? a;
    const cb = workCenters.data?.find((w) => w.id === b)?.code ?? b;
    return ca < cb ? -1 : ca > cb ? 1 : 0;
  });

  return (
    <section
      className="planning-screen planning-capacity"
      data-testid="planning-capacity"
      data-schedule-id={published.id}
      data-grain={grain}
    >
      {pageHead}
      {solve.banner}
      <ScheduleHatchDefs />
      <CapacityControls
        grain={grain}
        window={window}
        horizon={published}
        showDraft={search.draft === true}
        hasDraft={draft !== null}
        onChange={setSearch}
      />
      {allZero || noCalendars ? (
        <p className="planning-note" data-testid="planning-capacity-no-capacity">
          {t("planning.capacity.noCapacity")}{" "}
          <Link to="/planning/settings/calendars">{t("planning.capacity.openCalendars")}</Link>
        </p>
      ) : null}
      {showDraft && draftLoad.isPending ? (
        <CardSkeleton lines={3} label={t("planning.capacity.loading")} />
      ) : null}
      {showDraft && draftLoad.error !== null ? (
        <ErrorState
          title={t("planning.capacity.draftError")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => void draftLoad.refetch()}
          requestId={requestIdOf(draftLoad.error)}
          requestIdLabel={t("planning.common.requestId")}
        />
      ) : null}
      {ordered.length === 0 ? (
        <div data-testid="planning-capacity-empty">
          <EmptyState
            title={t("planning.capacity.emptyTitle")}
            body={t("planning.capacity.noRows")}
          />
        </div>
      ) : (
        ordered.map((wcId) => (
          <WorkCenterCard
            key={wcId}
            name={wcName(wcId)}
            workCenterId={wcId}
            rows={byWc.get(wcId) ?? []}
            deltas={deltaByWc?.get(wcId)}
            grain={grain}
          />
        ))
      )}
    </section>
  );
}

function CapacityControls({
  grain,
  window,
  horizon,
  showDraft,
  hasDraft,
  onChange,
}: {
  grain: CapacityGrain;
  window: { from: string; to: string };
  horizon: Pick<ScheduleHeader, "horizon_start" | "horizon_days" | "version">;
  showDraft: boolean;
  hasDraft: boolean;
  onChange: (next: Partial<CapacitySearch>) => void;
}) {
  const { t, i18n } = useTranslation();
  const last = addDaysIso(horizon.horizon_start, horizon.horizon_days - 1);
  return (
    <div className="planning-schedule-controls" data-testid="planning-capacity-controls">
      <div className="planning-schedule-window">
        <SegmentedControl
          label={t("planning.capacity.grainLabel")}
          data-testid="planning-capacity-grain"
          options={[
            { value: "week", label: t("planning.capacity.grain.week") },
            { value: "day", label: t("planning.capacity.grain.day") },
          ]}
          value={grain}
          onChange={(v) => {
            onChange({ grain: v === "day" ? "day" : "week", from: undefined });
          }}
        />
        {grain === "day" ? (
          <>
            <Button
              size="sm"
              data-testid="planning-capacity-earlier"
              disabled={window.from <= horizon.horizon_start}
              onClick={() => {
                onChange({ from: addDaysIso(window.from, -DAY_WINDOW) });
              }}
            >
              {t("planning.schedule.earlier")}
            </Button>
            <Button
              size="sm"
              data-testid="planning-capacity-later"
              disabled={window.to >= last}
              onClick={() => {
                onChange({ from: addDaysIso(window.from, DAY_WINDOW) });
              }}
            >
              {t("planning.schedule.later")}
            </Button>
          </>
        ) : null}
        <span
          className="planning-hint"
          data-testid="planning-capacity-range"
          data-from={window.from}
          data-to={window.to}
        >
          {t("planning.capacity.range", {
            version: horizon.version,
            from: formatDate(window.from, i18n.language),
            to: formatDate(window.to, i18n.language),
          })}
        </span>
      </div>
      {hasDraft ? (
        <CheckboxField
          label={t("planning.capacity.showDraft")}
          checked={showDraft}
          data-testid="planning-capacity-draft-toggle"
          onChange={(checked) => {
            onChange({ draft: checked ? true : undefined });
          }}
        />
      ) : null}
    </div>
  );
}

function WorkCenterCard({
  name,
  workCenterId,
  rows,
  deltas,
  grain,
}: {
  name: string;
  workCenterId: string;
  rows: readonly CapacityLoadRow[];
  deltas: readonly LoadRowLike[] | undefined;
  grain: CapacityGrain;
}) {
  const { t, i18n } = useTranslation();
  const deltaOf = useMemo(() => {
    const map = new Map<string, LoadRowLike>();
    for (const d of deltas ?? []) map.set(d.period_start, d);
    return map;
  }, [deltas]);
  return (
    <Card
      title={name}
      data-testid="planning-capacity-wc"
      {...{ "data-work-center-id": workCenterId }}
    >
      <ul className="planning-capacity-rows">
        {rows.map((row) => {
          const bar = barOf(row);
          const placed = row.setup_minutes + row.run_minutes;
          const over = row.overload_minutes > 0;
          const delta = deltaOf.get(row.period_start);
          const week = grain === "week" ? row.period_start : weekOf(row.period_start);
          return (
            <li
              key={row.period_start}
              className={over ? "planning-capacity-row is-over" : "planning-capacity-row"}
              data-testid="planning-capacity-row"
              data-period={row.period_start}
              data-capacity={row.capacity_minutes}
              data-setup={row.setup_minutes}
              data-run={row.run_minutes}
              data-due={row.due_minutes}
              data-proposed={row.proposed_minutes}
              data-overload={row.overload_minutes}
            >
              <span className="planning-capacity-period">
                {grain === "week"
                  ? weekLabel(row.period_start, i18n.language)
                  : formatDate(row.period_start, i18n.language)}
              </span>
              <span className="planning-capacity-bar" aria-hidden="true">
                <span
                  className="planning-capacity-setup"
                  style={{ width: `${String(bar.setup)}%` }}
                />
                <span
                  className="planning-capacity-run"
                  style={{ left: `${String(bar.setup)}%`, width: `${String(bar.run)}%` }}
                />
                {bar.proposed > 0 ? (
                  <Hatch
                    left={bar.setup + bar.run}
                    width={bar.proposed}
                    className="planning-hatched planning-capacity-proposed"
                  />
                ) : null}
                <span
                  className="planning-capacity-mark"
                  style={{ left: `${String(bar.capacity)}%` }}
                />
                {row.due_minutes > 0 ? (
                  <span className="planning-capacity-due" style={{ left: `${String(bar.due)}%` }} />
                ) : null}
              </span>
              <span className="planning-capacity-figures">
                {t("planning.capacity.figures", {
                  placed: formatHours(placed, i18n.language),
                  capacity: formatHours(row.capacity_minutes, i18n.language),
                  due: formatHours(row.due_minutes, i18n.language),
                })}
                {row.proposed_minutes > 0 ? (
                  <span className="planning-hint">
                    {" "}
                    {t("planning.capacity.proposed", {
                      hours: formatHours(row.proposed_minutes, i18n.language),
                    })}
                  </span>
                ) : null}
                {over ? (
                  <>
                    {" "}
                    <Link
                      to="/planning/schedule"
                      search={{ wc: workCenterId, from: week, days: 7 }}
                      className="planning-bad"
                      data-testid="planning-capacity-overload-link"
                    >
                      {t("planning.capacity.overload", {
                        hours: formatHours(row.overload_minutes, i18n.language),
                      })}
                    </Link>
                  </>
                ) : null}
                {delta !== undefined ? (
                  <span
                    className="planning-capacity-delta"
                    data-testid="planning-capacity-delta"
                    data-placed={delta.setup_minutes + delta.run_minutes}
                  >
                    {" "}
                    {t("planning.capacity.delta", {
                      hours: signedHours(delta.setup_minutes + delta.run_minutes, i18n.language),
                    })}
                  </span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function signedHours(minutes: number, locale: string): string {
  const text = formatHours(Math.abs(minutes), locale);
  return minutes > 0 ? `+${text}` : minutes < 0 ? `−${text}` : text;
}
